import React, { useEffect, useState } from "react";
import { api, dateFR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Pencil, Search, Download } from "lucide-react";
import { toast } from "sonner";
import { downloadXlsx, fmtDate, todayStamp } from "@/lib/csv";
import IdentityScannerBox from "@/components/IdentityScannerBox";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const empty = { nom: "", telephone: "", email: "", adresse: "", notes: "" };
const emptyAddr = { rue: "", code_postal: "", localite: "", delegation: "", gouvernorat: "", full: "" };

export default function Clients() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [addr, setAddr] = useState(emptyAddr);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/clients", { params: q ? { q } : {} });
    setItems(data);
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm(empty); setAddr(emptyAddr); setOpen(true); };
  const startEdit = (c) => {
    setEditing(c);
    setForm({ nom: c.nom || "", telephone: c.telephone || "", email: c.email || "", adresse: c.adresse || "", notes: c.notes || "" });
    // Pre-fill the rue field with the existing address text so user sees it; postal/loc/gouv left empty
    setAddr({ ...emptyAddr, rue: c.adresse || "", full: c.adresse || "" });
    setOpen(true);
  };

  const onAddrChange = (next) => {
    setAddr(next);
    setForm((f) => ({ ...f, adresse: next.full || next.rue || "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, adresse: addr.full || form.adresse };
      if (editing) {
        await api.put(`/clients/${editing.id}`, payload);
        toast.success("Client mis à jour");
      } else {
        await api.post("/clients", payload);
        toast.success("Client créé");
      }
      setOpen(false); load();
    } catch (e) { toast.error("Erreur lors de l'enregistrement"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer ${c.nom} ?`)) return;
    await api.delete(`/clients/${c.id}`);
    toast.success("Supprimé"); load();
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">
            Annuaire
          </div>
          <h1 className="font-display font-black text-4xl tracking-tighter mt-1">Clients</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="clients-search"
              type="search"
              name="clients-search"
              autoComplete="off"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Rechercher…"
              className="pl-9 rounded-sm w-64"
            />
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={startCreate} data-testid="client-add-btn" className="bg-klein hover:bg-blue-800 text-white rounded-sm">
                <Plus size={14} className="mr-1" /> Nouveau client
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight">
                  {editing ? "Modifier le client" : "Nouveau client"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <IdentityScannerBox
                  testIdPrefix="client-edit-id-scan"
                  compact
                  onExtract={(d) => {
                    const pieceParts = [];
                    if (d.type_piece) pieceParts.push(d.type_piece);
                    if (d.numero_piece) pieceParts.push(`N° ${d.numero_piece}`);
                    if (d.date_naissance) pieceParts.push(`Né(e) le ${d.date_naissance}`);
                    if (d.lieu_naissance) pieceParts.push(`à ${d.lieu_naissance}`);
                    if (d.date_expiration) pieceParts.push(`Expire ${d.date_expiration}`);
                    const pieceLine = pieceParts.join(" · ");
                    setForm((f) => ({
                      ...f,
                      nom: d.nom_complet || f.nom,
                      telephone: d.telephone || f.telephone,
                      email: d.email || f.email,
                      notes: pieceLine
                        ? (f.notes ? `${f.notes}\n${pieceLine}` : pieceLine)
                        : f.notes,
                    }));
                    if (d.adresse) {
                      setAddr((a) => {
                        const next = { ...a, rue: d.adresse };
                        next.full = [next.rue, next.localite, `${next.code_postal} ${next.delegation}`.trim(), next.gouvernorat]
                          .filter((s) => s && s !== " ")
                          .join(" · ");
                        return next;
                      });
                      setForm((f) => ({ ...f, adresse: d.adresse }));
                    }
                  }}
                />
                <div>
                  <Label className="text-xs uppercase tracking-wider">Nom *</Label>
                  <Input data-testid="client-form-nom" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} required className="mt-1 rounded-sm" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Téléphone *</Label>
                    <Input data-testid="client-form-tel" required value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} placeholder="Ex: 0612345678" className="mt-1 rounded-sm" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Email</Label>
                    <Input data-testid="client-form-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-sm" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Adresse</Label>
                  <div className="mt-1">
                    <AddressAutocomplete value={addr} onChange={onAddrChange} dataTestid="client-form-adresse" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Notes</Label>
                  <Textarea data-testid="client-form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-sm" rows={3} />
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="client-form-submit" className="bg-klein hover:bg-blue-800 text-white rounded-sm">
                    {editing ? "Enregistrer" : "Créer"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          <Button
            type="button"
            variant="outline"
            data-testid="clients-export-csv"
            onClick={() => {
              if (items.length === 0) { toast.error("Aucun client à exporter"); return; }
              const columns = [
                { key: "nom", label: "Nom" },
                { key: "telephone", label: "Téléphone" },
                { key: "email", label: "Email" },
                { key: "adresse", label: "Adresse" },
                { key: "notes", label: "Notes" },
                { key: "created_at", label: "Créé le", format: fmtDate },
              ];
              downloadXlsx(`clients-${todayStamp()}`, columns, items, "Clients");
              toast.success(`${items.length} client(s) exporté(s) (Excel)`);
            }}
            className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          >
            <Download size={14} className="mr-1" /> Excel
          </Button>
        </div>
      </header>

      <div className="bg-card border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-6 py-3">Nom</th>
              <th className="px-6 py-3">Téléphone</th>
              <th className="px-6 py-3">Email</th>
              <th className="px-6 py-3">Adresse</th>
              <th className="px-6 py-3">Créé le</th>
              <th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="clients-table-body">
            {loading && (
              <tr><td colSpan={6} className="px-6 py-10 text-center text-muted-foreground">Chargement…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-10 text-center text-muted-foreground">Aucun client. Créez-en un.</td></tr>
            )}
            {items.map((c) => (
              <tr key={c.id} className="border-t hover:bg-slate-50 transition-colors">
                <td className="px-6 py-3 font-medium">{c.nom}</td>
                <td className="px-6 py-3">{c.telephone || "—"}</td>
                <td className="px-6 py-3">{c.email || "—"}</td>
                <td className="px-6 py-3 max-w-xs truncate">{c.adresse || "—"}</td>
                <td className="px-6 py-3">{dateFR(c.created_at)}</td>
                <td className="px-6 py-3 text-right">
                  <button onClick={() => startEdit(c)} data-testid={`client-edit-${c.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs hover:bg-slate-100 rounded-sm mr-1">
                    <Pencil size={12} /> Modifier
                  </button>
                  <button onClick={() => remove(c)} data-testid={`client-delete-${c.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-destructive hover:bg-red-50 rounded-sm">
                    <Trash2 size={12} /> Supprimer
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
